import React, { createContext, useContext, useState, useEffect } from 'react';

export type BudgetTabType = 'budget' | 'financial' | 'analysis';

interface BudgetTabContextType {
  budgetId: string | null;
  activeTab: BudgetTabType;
  setBudgetId: (budgetId: string | null) => void;
  setActiveTab: (tab: BudgetTabType) => void;
  switchTab: (tab: BudgetTabType) => void;
}

const BudgetTabContext = createContext<BudgetTabContextType | undefined>(undefined);

export const useBudgetTabContext = () => {
  const context = useContext(BudgetTabContext);
  if (context === undefined) {
    throw new Error('useBudgetTabContext must be used within a BudgetTabProvider');
  }
  return context;
};

export const BudgetTabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [budgetId, setBudgetIdState] = useState<string | null>(() => {
    // 从localStorage恢复budgetId
    return localStorage.getItem('currentBudgetId');
  });
  
  const [activeTab, setActiveTabState] = useState<BudgetTabType>(() => {
    // 从localStorage恢复activeTab，默认为'budget'
    const saved = localStorage.getItem('currentBudgetTab');
    return (saved === 'budget' || saved === 'financial' || saved === 'analysis') ? saved : 'budget';
  });

  // 持久化budgetId到localStorage
  useEffect(() => {
    if (budgetId) {
      localStorage.setItem('currentBudgetId', budgetId);
    } else {
      localStorage.removeItem('currentBudgetId');
    }
  }, [budgetId]);

  // 持久化activeTab到localStorage
  useEffect(() => {
    localStorage.setItem('currentBudgetTab', activeTab);
  }, [activeTab]);

  const setBudgetId = (id: string | null) => {
    setBudgetIdState(id);
  };

  const setActiveTab = (tab: BudgetTabType) => {
    setActiveTabState(tab);
  };

  const switchTab = (tab: BudgetTabType) => {
    setActiveTabState(tab);
  };

  const value: BudgetTabContextType = {
    budgetId,
    activeTab,
    setBudgetId,
    setActiveTab,
    switchTab,
  };

  return <BudgetTabContext.Provider value={value}>{children}</BudgetTabContext.Provider>;
};
